/**
 * UebergabeTaskSection - the checklist of the task «Übergabe offener Vorgänge» (Mandatsauflösung, Stefan
 * P6 step 20): what is still open for the customer, read live from GET /tasks/:id/uebergabe next to the
 * counts the Freigabe saw. Closing the task is the task's own «Erledigen».
 */

import { useQuery } from '@tanstack/react-query';
import { PLACEMENT_STATUS_LABELS, formatDateDe } from '@brokly/shared';
import type { UebergabeChecklistDto, UebergabeChecklistItem } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { STATUS_LABEL as TASK_STATUS_LABEL } from '../../aufgaben/api/aufgaben-api';

type Group = keyof UebergabeChecklistDto['items'];

/** The German labels of the status codes per group (claim 0064, vorgang_status 0014, policy status). */
const STATUS_LABELS: Record<Group, Readonly<Record<string, string>>> = {
  placements: PLACEMENT_STATUS_LABELS,
  claims: { eingereicht: 'Eingereicht', in_bearbeitung: 'In Bearbeitung', abgeschlossen: 'Abgeschlossen' },
  tasks: TASK_STATUS_LABEL,
  vorgaenge: { versandt: 'Versandt', erinnert: 'Erinnert', ueberfaellig: 'Überfällig', eingegangen: 'Eingegangen', abgeschlossen: 'Abgeschlossen', abgebrochen: 'Abgebrochen' },
  policies: { active: 'Aktiv', in_mutation: 'In Mutation', renewal_pending: 'Erneuerung fällig' },
};

/** The document kinds a Vorgang carries (document_kind), as the label before its number. */
const VORGANG_KIND_LABELS: Readonly<Record<string, string>> = {
  mandat: 'Mandat',
  kuendigung_brokermandat: 'Kündigung Brokermandat',
  vertrag: 'Vertrag',
  korrespondenz: 'Korrespondenz',
};

/** A code's German label; an unknown code reads «Sonstiges» rather than leaking the code. */
function statusLabel(group: Group, code: string): string {
  return STATUS_LABELS[group][code] ?? 'Sonstiges';
}

const GROUPS: Array<{ key: Group; label: string }> = [
  { key: 'placements', label: 'Offene Platzierungen' },
  { key: 'claims', label: 'Offene Schäden' },
  { key: 'tasks', label: 'Offene Aufgaben' },
  { key: 'vorgaenge', label: 'Offene Vorgänge' },
  { key: 'policies', label: 'Policen' },
];

function ItemLine({ item, group }: { item: UebergabeChecklistItem; group: Group }) {
  const kind = group === 'vorgaenge' ? (item.kind ? (VORGANG_KIND_LABELS[item.kind] ?? 'Vorgang') : 'Vorgang') : null;
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm" data-testid={`uebergabe-item-${item.id}`}>
      <span>{kind ? `${kind} ${item.label}` : item.label}</span>
      {item.status && <span className="text-xs text-muted-foreground">{statusLabel(group, item.status)}</span>}
      {group === 'policies' && item.courtage_eligible_to && (
        <span className="text-xs">Courtageberechtigt bis {formatDateDe(item.courtage_eligible_to)}</span>
      )}
    </li>
  );
}

export function UebergabeTaskSection({ taskId }: { taskId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: qk.taskUebergabe(taskId),
    queryFn: () => apiClient.tasks.uebergabe(taskId),
  });
  if (isLoading) return <p className="text-sm text-muted-foreground">Lade Checkliste …</p>;
  if (isError || !data) return <p className="text-sm text-status-danger">Die Checkliste konnte nicht geladen werden.</p>;
  return (
    <div className="flex flex-col gap-3" data-testid="uebergabe-section">
      {data.end_date && (
        <p className="text-xs text-muted-foreground">
          {data.process_number ? `${data.process_number} · ` : ''}Mandat endet am {formatDateDe(data.end_date)}
        </p>
      )}
      {GROUPS.map((g) => {
        const items = data.items[g.key];
        return (
          <div key={g.key} data-testid={`uebergabe-group-${g.key}`}>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">
              {g.label} ({items.length})
            </h4>
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground">Keine.</p>
            ) : (
              <ul className="flex flex-col gap-1">
                {items.map((it) => (
                  <ItemLine key={it.id} item={it} group={g.key} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
